import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FaSearch, FaTimes, FaCommentDots } from 'react-icons/fa';
import { formatAuPhone } from './conversationFormat';

// Start a thread with a franchisee who has not texted in yet. Without this the admin could only ever
// reply, never open a one-to-one conversation, which is the more common direction in practice.
// Only franchisees with a mobile number are listed, because SMS is the only channel here.

const NewConversationModal = ({ isOpen, franchisees, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      searchRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    return franchisees
      .filter((f) => f.phoneNumber)
      .filter((f) => !query || [f.firstName, f.lastName, f.franchiseName, f.fseCode, f.phoneNumber]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)));
  }, [franchisees, search]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-conversation-title"
      onClick={onClose}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 id="new-conversation-title" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FaCommentDots className="text-jims-cyan" /> New conversation
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan rounded"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search franchisees…"
              aria-label="Search franchisees"
              className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan"
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">Only franchisees with a mobile number are shown.</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {matches.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-500">No franchisees match your search.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {matches.map((franchisee) => (
                <li key={franchisee.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(franchisee)}
                    className="w-full text-left px-5 py-3 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-jims-cyan"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-gray-900 truncate">
                        {[franchisee.firstName, franchisee.lastName].filter(Boolean).join(' ') || franchisee.franchiseName || franchisee.fseCode}
                      </span>
                      <span className="shrink-0 text-xs text-gray-500">{franchisee.fseCode}</span>
                    </div>
                    <div className="text-sm text-gray-500">{formatAuPhone(franchisee.phoneNumber) || franchisee.phoneNumber}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewConversationModal;
